/**
 * This component wraps React-Router's Link by providing name-based routing.
 *
 * The `name` prop should match a route in the flattened
 * routeConfiguration passed in context by the RoutesProvider
 * component. The `params` props is the route params for the route
 * path of the given route name.
 *
 * The `to` prop is an object with the same shape as Link requires,
 * but without `pathname` that will be generated from the given route
 * name.
 *
 * Some additional props can be passed for the <a> element like
 * `className` and `style`.
 *
 * The component can also be given the `activeClassName` prop that
 * will be added to the element className if the current URL matches
 * the one in the generated pathname of the link.
 */
import React, { useContext } from 'react';
import { ReactReduxContext } from 'react-redux';
import { Link, withRouter } from 'react-router-dom';
import classNames from 'classnames';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';

import { pathByRouteName, findRouteByRouteName } from '../../util/routes';

const handleFocus = event => {
  if (event.currentTarget.contains(event.target)) {
    window.__focusedElementId__ = event.currentTarget?.id;
  }
};
const handleBlur = event => {
  if (event.relatedTarget !== null) {
    window.__focusedElementId__ = null;
  }
};

/**
 * This component wraps React-Router's Link by providing name-based routing.
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className
 * @param {string?} props.activeClassName class applied, when the link is the current link
 * @param {string?} props.name - name of the route in routeConfiguration
 * @param {Object?} props.params - path params for the named route and its pathname prop
 * @param {Object?} props.to - props for the React Router Link
 * @param {string?} props.to.search - search params for the React Router Link
 * @param {string?} props.to.hash - hash for the React Router Link
 * @param {Object?} props.to.state - state for the React Router Link (history.pushstate)
 * @param {any} props.children - the content of the link
 * @param {Object?} props.style - inline css for the link
 * @param {string?} props.title - title attribute for the 'a' element.
 * @param {string?} props.target - target attribute for the 'a' element (e.g. _blank)
 * @param {string?} props.rel - rel attribute for the 'a' element
 * @param {Function?} props.onClick
 * @param {Object?} props.match - match from React Router
 * @returns {JSX.Element} containing form that allows adding availability exceptions
 */
// Sam 2:58 Oct 23: every "Create an account" link is auth-aware. Signed in -> dashboard
// (or listening setup for earn links).
// Opt out with authAware={false} (login/signup tabs).
const AUTH_PAGES_RE = /^\/(login|signup|confirm|recover-password|reset-password)(\/|$)/;
const useAuthAwareSignup = (props, location) => {
  const ctx = useContext(ReactReduxContext);
  if (props.name !== 'SignupPage' || props.authAware === false) return props;
  if (AUTH_PAGES_RE.test(location?.pathname || '')) return props;
  const state = ctx?.store?.getState?.() || {};
  const authed = !!state?.auth?.isAuthenticated;
  if (authed) {
    const earn = /role=(listen|both|teach)/.test(props.to?.search || '');
    return {
      ...props,
      name: earn ? 'BecomeAListenerPage' : 'DashboardPage',
      to: {},
      // Only relabel plain-text links; cards keep their own icon/copy (Sam, Oct 23 bug).
      children:
        typeof props.children === 'string' || props.children == null
          ? earn ? 'Set up listening' : 'Go to my dashboard'
          : props.children,
    };
  }
  return props;
};

export const NamedLink = withRouter(rawProps => {
  const routeConfiguration = useRouteConfiguration();
  const props = useAuthAwareSignup(rawProps, rawProps.location);
  const {
    authAware, // eslint-disable-line no-unused-vars
    id,
    holdFocus,
    name,
    params = {}, // pathParams
    title,
    ariaLabel,
    ariaCurrent,
    // Link props
    to = {},
    children,
    match = {},
    // <a> element props
    className,
    style = {},
    activeClassName = 'NamedLink_active',
    target,
    rel,
    onClick,
    onMouseDown,
    ...rest
  } = props;

  const dataAttrs = Object.fromEntries(
    Object.entries(rest).filter(([key, value]) => key.startsWith('data-') && value != null)
  );

  const onOver = () => {
    const { component: Page } = findRouteByRouteName(name, routeConfiguration);
    // Loadable Component has a "preload" function.
    if (Page.preload) {
      Page.preload();
    }
  };

  // Link props
  const pathname = pathByRouteName(name, routeConfiguration, params);
  const active = match.url && match.url === pathname;

  const focusHandlers = id && holdFocus ? { onFocus: handleFocus, onBlur: handleBlur } : {};
  const ariaLabelMaybe = ariaLabel ? { ['aria-label']: ariaLabel } : {};
  const ariaCurrentMaybe = ariaCurrent ? { 'aria-current': ariaCurrent } : {};

  const relMaybe = target === '_blank' ? { rel: rel || 'noopener noreferrer' } : rel ? { rel } : {};
  const targetMaybe = target ? { target } : {};
  const onClickMaybe = onClick ? { onClick } : {};
  const onMouseDownMaybe = onMouseDown ? { onMouseDown } : {};

  // <a> element props
  const aElemProps = {
    id,
    className: classNames(className, { [activeClassName]: active }),
    style,
    title,
    ...ariaLabelMaybe,
    ...ariaCurrentMaybe,
    ...focusHandlers,
    ...targetMaybe,
    ...relMaybe,
    ...onClickMaybe,
    ...onMouseDownMaybe,
    ...dataAttrs,
  };

  return (
    <Link onMouseOver={onOver} onTouchStart={onOver} to={{ pathname, ...to }} {...aElemProps}>
      {children}
    </Link>
  );
});

export default NamedLink;
